import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import translateLabelsInArray from 'i18n/helpers/translateLabelsInArray';
import { required } from 'utils/validation/fieldValidationRules';
import TPGrid from 'components/TP-UI/TPGrid';
import TPSelect from 'components/TP-UI/TPSelect';
import TPAlert from 'components/TP-UI/TPAlert';
import TPButton from 'components/TP-UI/TPButton';
import TPErrorMessage from 'components/TP-UI/TPErrorMessage';
import TPCard from 'components/TP-UI/TPCard';
import TPCardContent from 'components/TP-UI/TPCard/components/TPCardContent';
import TPCardTitle from 'components/TP-UI/TPCard/components/TPCardTitle';
import TPCardDescription from 'components/TP-UI/TPCard/components/TPCardDescription';
import CurrencySelect from 'modules/common/CurrencySelect';
import AmountTextField from 'modules/common/AmountTextField';
import SymbolGroupForm from '../SymbolGroupForm';
import Results from '../Results';
import { TRADE_VALUES } from '../../constants';
import useStyles from './styles';

const ProfitLossCalculatorForm = ({
  form,
  loading,
  symbols,
  contractSize,
  currencies,
  baseCurrency,
  results,
  error = null,
  handleSubmit,
}) => {
  const { t, i18n } = useTranslation('tradingCalculators');
  const classes = useStyles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tradeValues = useMemo(() => translateLabelsInArray(TRADE_VALUES), [i18n.language]);
  const descriptionItems = useMemo(
    () => [
      [
        {
          dataKey: 'profitLossSymbol',
          label: t(`results.profitLossSymbol`),
        },
        {
          dataKey: 'profitLossBase',
          label: t(`results.profitLossBase`),
        },
        {
          dataKey: 'conversionRate',
          label: t(`results.conversionRate`),
        },
        {
          dataKey: 'pipValue',
          label: t(`results.pipValue`),
        },
      ],
    ],
    [t],
  );

  return (
    <>
      <TPGrid container spacing={3}>
        <TPGrid item>
          <TPAlert variant="info">{t('profitLossCalculator.description')}</TPAlert>
        </TPGrid>
        <TPGrid item container spacing={2}>
          <TPGrid item xs={12} md={6}>
            <TPCard stretch>
              <TPCardContent>
                <TPCardTitle>{t('calculatorTitle')}</TPCardTitle>
                <TPCardDescription>{t('calculatorDescription')}</TPCardDescription>
                <form noValidate className={classes.form} onSubmit={handleSubmit}>
                  <TPGrid container spacing={3} direction="column">
                    {error && (
                      <TPGrid item xs={12}>
                        <TPErrorMessage error={error} size="medium" />
                      </TPGrid>
                    )}
                    <TPGrid item container direction="row" spacing={2}>
                      <TPGrid item xs={12}>
                        <SymbolGroupForm
                          form={form}
                          symbols={symbols}
                          contractSize={contractSize}
                        />
                      </TPGrid>
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={CurrencySelect}
                          id="baseCurrency"
                          name="baseCurrency"
                          label={t('fields.baseCurrency')}
                          placeholder={t('placeholders.baseCurrency')}
                          options={currencies}
                          required
                          fullWidth
                          validate={[required]}
                        />
                      </TPGrid>
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={AmountTextField}
                          id="openPrice"
                          name="openPrice"
                          label={t('fields.openPrice')}
                          placeholder={t('placeholders.openPrice')}
                          type="number"
                          currency={baseCurrency}
                          required
                          fullWidth
                          validate={[required]}
                        />
                      </TPGrid>
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={AmountTextField}
                          id="closePrice"
                          name="closePrice"
                          label={t('fields.closePrice')}
                          placeholder={t('placeholders.closePrice')}
                          type="number"
                          currency={baseCurrency}
                          required
                          fullWidth
                          validate={[required]}
                        />
                      </TPGrid>
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={AmountTextField}
                          id="volumeInLots"
                          name="volumeInLots"
                          label={t('fields.volumeInLots')}
                          placeholder={t('placeholders.volumeInLots')}
                          type="number"
                          currency={baseCurrency}
                          required
                          fullWidth
                          validate={[required]}
                        />
                      </TPGrid>
                      <TPGrid item xs={12} md={6}>
                        <Field
                          component={TPSelect}
                          id="tradeType"
                          name="tradeType"
                          label={t('fields.tradeType')}
                          placeholder={t('placeholders.tradeType')}
                          type="number"
                          options={tradeValues}
                          required
                          fullWidth
                          validate={[required]}
                        />
                      </TPGrid>
                      <TPGrid item container justifyContent="flex-end">
                        <TPButton
                          primary
                          type="submit"
                          size="large"
                          loading={loading}
                          fullWidth={isMobileOnly}>
                          {t('buttons.calculate')}
                        </TPButton>
                      </TPGrid>
                    </TPGrid>
                  </TPGrid>
                </form>
              </TPCardContent>
            </TPCard>
          </TPGrid>
          <TPGrid item xs={12} md={6}>
            <Results loading={loading} results={results} descriptionItems={descriptionItems} />
          </TPGrid>
        </TPGrid>
      </TPGrid>
    </>
  );
};

ProfitLossCalculatorForm.propTypes = {
  form: PropTypes.string.isRequired,
  symbols: PropTypes.array.isRequired,
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      flag: PropTypes.string.isRequired,
    }),
  ).isRequired,
  contractSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  baseCurrency: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

export default reduxForm()(ProfitLossCalculatorForm);
